import { BirthWishViewModel } from "../ViewModels";
import { useBirthWishEnricher } from "../Hooks";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainDetailHook,
    DomainHookProps,
    useDetailLifeCycle,
} from "@/Shared";

import { useBirthWish } from "@/OneLiners";
import { BirthWishModel } from "../BirthWishModel";


/**
 * useBirthWishDetail hook
 *
 * Domain-specific detail hook for BirthWish entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the BirthWishViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, and navigation methods to the UI layer.
 *
 * @see DomainHookProps
 * @see DomainDetailHook<BirthWishViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<BirthWishViewModel>
 */
export function useBirthWishDetail(props : DomainHookProps): DomainDetailHook<BirthWishViewModel> {
     const {permissionHook, accessRight, extraArgs} = props;
    const { domainType } = DOMAIN_CONFIGS.BirthWish;
    const {entity} = extraArgs ?? {};


    /**
     * BirthWish domain CRUD operations and state
     */
    const birthWishHook = useBirthWish();

    /**
     * BirthWish domain data and operations
     * Provided by the birthWish enricher facade
     */
    const {
        enricherResponse,
        getStrictEnrichedBirthWish,
    } = useBirthWishEnricher();

    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            birthWishHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: birthWishHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: birthWishHook,
        buildVM: (currentEntity: BirthWishModel, isDisplaying: boolean) => {
            return new BirthWishViewModel({
                birthWish: getStrictEnrichedBirthWish(currentEntity),
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}